import type { DismissalDuration } from '@fig/shared';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/theme-provider';

const DURATION_OPTIONS: Array<{ value: DismissalDuration; label: string }> = [
  { value: '3_months', label: '3 months' },
  { value: '6_months', label: '6 months' },
  { value: '1_year', label: '1 year' },
  { value: 'never', label: 'Never' },
];

interface DismissalModalProps {
  open: boolean;
  title: string;
  description: string;
  commentLabel: string;
  commentPlaceholder: string;
  confirmLabel?: string;
  isSubmitting?: boolean;
  showDurationOptions?: boolean;
  subjectLabel?: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { comment: string; duration?: DismissalDuration }) => void | Promise<void>;
}

export function DismissalModal({
  open,
  title,
  description,
  commentLabel,
  commentPlaceholder,
  confirmLabel = 'Confirm',
  isSubmitting = false,
  showDurationOptions = false,
  subjectLabel,
  onOpenChange,
  onConfirm,
}: DismissalModalProps) {
  const { theme } = useTheme();
  const [comment, setComment] = useState('');
  const [duration, setDuration] = useState<DismissalDuration | null>(
    showDurationOptions ? '3_months' : null
  );

  const isDark = theme === 'dark';
  const modalSurface = isDark ? 'hsl(210 30% 12%)' : 'hsl(0 0% 100%)';
  const fieldSurface = isDark ? 'hsl(210 35% 9%)' : 'hsl(210 15% 96%)';
  const borderColor = isDark ? 'hsl(210 25% 24%)' : 'hsl(210 15% 82%)';
  const textColor = isDark ? 'hsl(210 15% 93%)' : 'hsl(210 50% 10%)';
  const primaryColor = isDark ? 'hsl(201 45% 55%)' : 'hsl(211 47% 25%)';
  const primaryTextColor = isDark ? 'hsl(210 80% 8%)' : 'hsl(0 0% 98%)';

  useEffect(() => {
    if (!open) {
      setComment('');
      setDuration(showDurationOptions ? '3_months' : null);
    }
  }, [open, showDurationOptions]);

  const trimmedComment = comment.trim();
  const canSubmit = trimmedComment.length > 0 && (!showDurationOptions || duration !== null);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onConfirm({
      comment: trimmedComment,
      duration: duration ?? undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fig-dismissal-modal border border-border bg-surface-3 text-foreground shadow-elevation-3 sm:max-w-xl"
        data-theme={theme}
        style={{
          backgroundColor: modalSurface,
          borderColor,
          color: textColor,
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {subjectLabel && (
            <div
              className="fig-dismissal-panel rounded-lg border border-border bg-muted/30 px-3 py-2"
              style={{ backgroundColor: fieldSurface, borderColor }}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Product
              </p>
              <p className="mt-1 text-sm font-medium text-foreground">{subjectLabel}</p>
            </div>
          )}

          {showDurationOptions && (
            <div className="space-y-2">
              <Label>Dismiss for</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      'rounded-md border px-3 py-2 text-sm font-medium transition-all',
                      duration === option.value
                        ? 'border-primary bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/25'
                        : 'fig-dismissal-field border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    style={
                      duration === option.value
                        ? {
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            color: primaryTextColor,
                          }
                        : { backgroundColor: fieldSurface, borderColor, color: textColor }
                    }
                    onClick={() => setDuration(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="dismissal-comment">{commentLabel}</Label>
            <textarea
              id="dismissal-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={commentPlaceholder}
              maxLength={2000}
              className="fig-dismissal-field min-h-[8rem] w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring"
              style={{ backgroundColor: fieldSurface, borderColor, color: textColor }}
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              {trimmedComment.length}/2000
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            className="bg-primary text-primary-foreground shadow-sm hover:opacity-90"
            style={{
              backgroundColor: primaryColor,
              color: primaryTextColor,
            }}
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
